import React, { useState, useEffect, useRef, useContext } from "react";
import { ReviewsContext, ReviewsProps } from "./Context";
import { useDispatch } from "react-redux";
import {renderRatingStars,
    isDateAfter, searchContent,
    formatDateForComparison, subtractDate, valueExists } from '../../utils/utilsHelpers';
import {incrementItemCount, sortDataViaKey,
    getBulkData, onFilterSelect,
    onChipRemove, loadMoreHandler } from '../../utils/filtersHelpers';
import { updateReviewAccessCount } from "../../utils/postingHelpers";
import { range } from "../../../javascripts/utils";
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col'
import Button from 'react-bootstrap/Button';
import Filters from '../filters/Filters';
import FiltersTags from "../filters/FiltersTags";
import SlideBar from '../SlideBar';
import ReviewTagging from "./ReviewTagging";
import ReviewCard from './ReviewCard';
import ReviewModal from "./ReviewModal";
import ReviewTableList from "./ReviewTableList";
import Collapse from "react-bootstrap/Collapse";
import LeadGen from "../leads/LeadGen";
import { createLead } from "javascripts/utils/createLead.js";
import {isMobile, isDesktop} from "react-device-detect";

import {deleteQuote, getTags} from '../../actions/taggingDataActions';
import {JSON_HEADERS, COMPANY_NAME, LEADGEN_TOGGLE_RESET_TIME} from "../../constants/constants";
import SearchBar from "../SearchBar";
import SelectFilter from "../customFilters/SelectFilter";
import LoadingModal from "../modals/Loading";
import { captureImpression } from 'javascripts/impressions/capture_impression.js';
import Placeholder from "react-bootstrap/Placeholder";
import Form from "react-bootstrap/Form";
import PopoverTrigger from "../bootstrap/PopoverTrigger";
import ReviewResearchTable from "./ReviewResearchTable";
import RegistrationRoot from "../users/RegistrationRoot";
import ResponseModal from "../modals/ResponseModal";
import ReportBanner from "../leads/ReportBanner";
import AudioModal from "../modals/AudioModal";

function Reviews() {
    const props = useContext(ReviewsProps);
    const dispatch = useDispatch();
    const [shownReviews, setShownReviews] = useState(null);
    const [sortToggle, setSortToggle] = useState(false);
    const [bulkCounter, setBulkCounter] = useState(0);
    const [isUpdatingReviews, setIsUpdatingReviews] = useState(false);
    const [isLoadingReviews, setIsLoadingReviews] = useState(false);
    const [fetchReviews, setFetchReviews] = useState(!!props.fetch_filtered_reviews);
    const [allReviews, setAllReviews] = useState(fetchReviews ? [] : props.reviews);
    const [filteredReviews, setFilteredReviews] = useState(props.reviews);
    const [selectedFilters, setSelectedFilters] = useState({dropdown: {}});
    const [showFilterForm, setShowFilterForm] = useState(!isMobile);

    const [filtersMapping, setFiltersMapping] = useState({});
    const [searchValue, setSearchValue] = useState("");
    const [searchIcon, setSearchIcon] = useState('magnifying-glass');
    const [searchViaElastic, setSearchViaElastic] = useState(false);
    const [resetSort, setResetSort] = useState(false);
    const [sortBy, setSortBy] = useState({ key: 'score', ascend: sortToggle });
    const [openFilter, setOpenFilter] = useState(null);
    const [openFilterReviews, setOpenFilterReviews] = useState(null);

    const [slideBarToggle, setSlideBarToggle] = useState(false);

    const [selectedReview, setSelectedReview] = useState(props.sharedReview || null);
    const [cacheReviewID, setCacheReviewID] = useState(0);
    const [isShareLink, setIsShareLink] = useState(!!props.sharedReview);
    const [showReviewModal, setShowReviewModal] = useState(!!props.sharedReview);
    const [selectedReviewHistory, setSelectedReviewHistory] = useState([]);
    const [hasMoreReviews, setHasMoreReviews] = useState(true);
    const [isForwardClick, setIsForwardClick] = useState(false);

    // test restricted access to reviews
    const [reviewAccessCount, setReviewAccessCount] = useState(props.review_access_count || (showReviewModal? 1 : 0));
    const [showRegistrationModal, setShowRegistrationModal] = useState(false);
    const [registrationRequired, setRegistrationRequired] = useState(props.registration_required);
    const [currentUser, setCurrentUser] = useState(props.current_user);
    const [showRegistrationConfirmation, setShowRegistrationConfirmation] = useState(false);
    const [leadAttributes, setLeadAttributes] = useState(props.lead_attributes);
    const [showLeadGen, setShowLeadGen] = useState(false);
    const [leadDetails, setLeadDetails] = useState({});

    const timeframeData = [3,6,9,12,24];
    const timeframeSuffix = ' months ago';
    const renderAsTable = (props.render_as === 'vendor_dashboard' || props.render_as === 'research')
    const bulkSize = renderAsTable ? 25 : 8;
    const tooltips = props.tooltips;

    const [selectedAudio, setSelectedAudio] = useState({});
    const [showAudioModal, setShowAudioModal] = useState(false);

    const reviewsContainerRef = useRef(null);

    useEffect(() => {
        if (isShareLink) {
            captureImpression(selectedReview.actions.url);
        }
    }, []);

    useEffect(() => {
        // reset if we're switching between products in peeranalyst
        if(allReviews.length) {
            setAllReviews(props.reviews);
            setFilteredReviews(props.reviews);
            onFiltersChangeHandler({dropdown: {}}, '', props.reviews);
        }
    }, [props.reviews]);

    useEffect(() => {
        // if user triggers the modal, start to get all the reviews
        if(showReviewModal && fetchReviews) {
            fetchInitialReviews();
        }
        // testing restricted review access
        if(registrationRequired && showReviewModal && reviewAccessCount) {
            updateReviewAccessCount(props.token);
            setTimeout(() => setShowRegistrationModal(true), 3000);
        }
    }, [showReviewModal, reviewAccessCount]);

    const filtersMappingBase = {
        dropdown: props.filters || {
            'industry': null,
            'company_size': null,
            'rating': null,
            'sentiment': null,
            'job_level': null,
            'considered': null,
            'timeframe': null,
            'company_name': null,
            'strategic_themes': null
        }
    }

    const sortingBase = [
        { key: 'score', name: 'PeerSpot Review Score', ascend: false, tooltipText: "Our review score considers factors like product usage duration, the depth of detail regarding the use case, and feature insights. Comprehensive and recent reviews are prioritized for relevance and accuracy." },
        { key: 'published_date', name: 'Most recent', ascend: false },
        { key: 'word_count', name: 'Review length', ascend: false },
        { key: 'rating', name: 'Highest rating', ascend: false },
        { key: 'rating', name: 'Lowest rating', ascend: true }
    ]

    const fetchInitialReviews = async () => {
        setFetchReviews(false);
        try {
            const response = await fetch(`/products/${props.product_id}/reviews_for_filter_and_initial_display`, {
                method: 'GET',
                headers: JSON_HEADERS,
            });
            const jsonResponse = await response.json();
            if (jsonResponse.reviews) {
                setAllReviews(jsonResponse.reviews);
                setFilteredReviews(jsonResponse.reviews);
                onFiltersChangeHandler(selectedFilters, '', jsonResponse.reviews);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchSingleReview = async (reviewId) => {
        let data = { include_data: 'display_data,lead_gen_data', review_ids: [reviewId] };
        await fetch('/product_reviews/presenter_data', {
            method: 'POST',
            headers: JSON_HEADERS,
            body: JSON.stringify(data)
        }).then(function(response) {
            return response.json();
        }).then(function(jsonResponse) {
            let review = jsonResponse.reviews?.[0];
            review && captureImpression(review.actions.url);
            setSelectedReviewHistory([...selectedReviewHistory, selectedReview]);
            setSelectedReview(review);
            setShowReviewModal(true);
        });
    };

    // Once reviews are available, filter them
    useEffect(() => {
        if(!allReviews.length && fetchReviews) {

            // We're now displaying the first 8 reviews on load, without the filters
            setShownReviews(props.reviews);

            const observer = new IntersectionObserver((entries, observer) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        fetchInitialReviews();
                        observer.unobserve(entry.target); // Stop observing once loaded
                    }
                });
            }, { threshold: 0.1 });

            if (reviewsContainerRef.current) {
                observer.observe(reviewsContainerRef.current);

                // Check if the reviewsContainerRef is in view
                const { top } = reviewsContainerRef.current.getBoundingClientRect();
                if (top <= window.innerHeight) {
                    fetchInitialReviews();
                    observer.unobserve(reviewsContainerRef.current);
                }
            }

            return () => observer?.disconnect(); // Clean up

        } else if(shownReviews == null) {
            onFiltersChangeHandler(selectedFilters);
        }
    });

    const handleReviewModalToggle = (e) => {
        if (e.target.tagName === 'A' && e.target.search && new URLSearchParams(e.target.search).has('review_id')) {
            e.preventDefault();
            const reviewId = +new URLSearchParams(e.target.search).get('review_id');
            let review = props.reviews.find((r) => r.id === reviewId) || allReviews.find((r) => r.id === reviewId);
            if(review?.actions) {
                review && captureImpression(review.actions.url);
                setSelectedReview(review);
                setShowReviewModal(true);
            } else {
                fetchSingleReview(reviewId);
            }
            // restricted review access
            setReviewAccessCount((c) => c + 1);
        }
    }

    const handleReviewModalToggleOnForward = (reviewId) => {
        if(reviewId) {
            let review = props.reviews.find((r) => r.id === reviewId) || allReviews.find((r) => r.id === reviewId) || props.sharedReview;
            setSelectedReview(review);
            setShowReviewModal(true);
            setIsForwardClick(true);
            // Do we want to limit review access on forward?
        }
    }

    useEffect(() => {
        // Attach event listener to document to handle a review modal toggle outside of the react app
        document.addEventListener('click', handleReviewModalToggle);
    }, []);

    const onResetFilters = () => {
        setSearchValue('');
        setResetSort(!resetSort);
        setFilteredReviews(allReviews);
        setSelectedFilters({dropdown: {}});
        setShownReviews(null);
    }

    // Populate filters with items count based on selected reviews and search value
    const populateFiltersMapping = (reviews = allReviews, searchVal, selectedFilters) => {
        Object.keys(filtersMappingBase).forEach((filterType) => {
            Object.keys(filtersMappingBase[filterType]).forEach((filter, index) => {
                filtersMappingBase[filterType][filter] = {};

                const isOnlySelectedFilter = (selectedFilters.dropdown[filter] &&
                  Object.keys(selectedFilters.dropdown).length === 1 &&
                  !searchVal)
                const isOpenFilter = filter === openFilter;
                let relevantReviews;
                if (isOnlySelectedFilter) {
                    relevantReviews = allReviews;
                } else if (isOpenFilter && Object.keys(selectedFilters.dropdown).length) {
                    relevantReviews = openFilterReviews;
                } else {
                    relevantReviews = reviews;
                }
                switch (filter) {
                    case 'company_name':
                        let label = 'Company name listed';
                        relevantReviews.forEach((review) => {
                            if (review.company) {
                                incrementItemCount(filtersMappingBase[filterType][filter], label);
                            }
                        });
                        return;
                    case 'timeframe':
                        relevantReviews.forEach((review) => {
                            timeframeData.forEach((timeAmount, index) => {
                                const timeframe = formatDateForComparison(subtractDate(timeAmount, 'months'));
                                let label = `${timeAmount}${timeframeSuffix}`;
                                if (isDateAfter(formatDateForComparison(review.published_date), timeframe)) {
                                    incrementItemCount(filtersMappingBase[filterType][filter], label);
                                }
                            })
                        })
                        return;
                    case 'considered':
                        relevantReviews.forEach((review) => {
                            let val = review[filter]?.toString();
                            props.considered_list?.forEach((item) => {
                                if (val.includes(item)) {
                                    incrementItemCount(filtersMappingBase[filterType][filter], item);
                                }
                            })
                        })
                        return;
                    case 'sentiment':
                        // group sentiment scores by their whole number value (7.4 -> 7) to display in the filters and calculate the number of reviews that fall under each filter option
                        relevantReviews.forEach((review) => {
                            let val = valueExists(review[filter]) ? Math.floor(review[filter])?.toString() : null;
                            if(val) {
                                range(0,10).forEach((n) => {
                                    let item = n.toString();
                                    if (val === item) {
                                        incrementItemCount(filtersMappingBase[filterType][filter], item);
                                    }
                                })
                            }
                        })
                        return;
                    case 'strategic_themes':
                        relevantReviews.forEach((review) => {
                            // structure [[theme1, quote],[theme2,quote],[theme1,quote]] - can have multiple quotes per theme
                            let review_themes = review[filter]?.map(arr => arr[0]) || [];
                            if(review_themes.length > 0) {
                                props?.strategic_themes.forEach((theme) => {
                                    if (review_themes.includes(theme)) {
                                        incrementItemCount(filtersMappingBase[filterType][filter], theme);
                                    }
                                })
                            }
                        })
                        return;
                    default:
                        relevantReviews.forEach((review) => {
                            let val = review[filter]?.toString();
                            if (val?.length > 0) {
                                incrementItemCount(filtersMappingBase[filterType][filter], val);
                            }
                        })
                        return;
                }
            })
        })

        const sortedFilters = sortFilters(filtersMappingBase);
        setFiltersMapping(sortedFilters)
    }

    // Sort filters based on updated filters mapping
    const sortFilters = (filtersMapping) => {
        let sortedFiltersMapping = {};
        Object.keys(filtersMapping).forEach((filterType) => {
            sortedFiltersMapping[filterType] = {};
            Object.keys(filtersMapping[filterType]).forEach((filter) => {
                sortedFiltersMapping[filterType][filter] = [];

                let sortedItems = [];
                switch (filter) {
                    case 'timeframe':
                        timeframeData.forEach((timeframeItem) => {
                            let label = `${timeframeItem}${timeframeSuffix}`;
                            if (filtersMapping[filterType][filter][label]) {
                                sortedItems.push(label);
                            }
                        })
                        break;
                    default:
                        sortedItems = Object.keys(filtersMapping[filterType][filter]).sort((a,b) => {
                            if (a < b) return -1;
                            if (b < a) return 1;
                            return 0;
                        })
                        break;
                }

                if(filter === 'company_size') sortedItems.reverse();

                sortedItems.map((item) => {
                    let renderObject = null;
                    if (filter === 'rating') {
                        renderObject = renderRatingStars(item);
                    }
                    sortedFiltersMapping[filterType][filter].push({label: item, sum: filtersMapping[filterType][filter][item], renderObject: renderObject, value: item })
                })
            })
        })

        return sortedFiltersMapping;
    }

    // Get bulk data from filtered reviews
    const getBulkDataHandler = async(reviews, isNewBulk = false) => {
        if(!reviews.length) {
            setShownReviews(null);
            return;
        }
        const bulkData = getBulkData(reviews, shownReviews, bulkCounter, bulkSize, isNewBulk);
        if(!renderAsTable) {
            // check if we have all the display data we need
            let incompleteReviews = bulkData.filter(review => review.title === undefined);
            if(incompleteReviews.length) {
                setIsUpdatingReviews(isNewBulk);
                setIsLoadingReviews(!isNewBulk);
                let data = { include_data: 'display_data,lead_gen_data', review_ids: incompleteReviews.map(review => review.id) };
                await fetch('/product_reviews/presenter_data', {
                    method: 'POST',
                    headers: JSON_HEADERS,
                    body: JSON.stringify(data)
                }).then(function(response) {
                    return response.json();
                }).then(function(jsonResponse) {
                    if(jsonResponse.reviews) {
                        // reset original reviews
                        let reviewsPopulated = allReviews.map((review) => {
                            let reviewData = jsonResponse.reviews.find((r) => r.id === review.id);
                            return { ...review, ...reviewData }
                        });
                        setAllReviews(reviewsPopulated);
                        // reset filtered and sorted reviews
                        let newFilteredReviews = reviews.map((r) => {
                            return reviewsPopulated.find((rv) => rv.id === r.id)
                        });
                        setFilteredReviews(newFilteredReviews);
                        // reset bulk data
                        let filteredIds = bulkData.map((r) => r.id);
                        let newBulkData = newFilteredReviews.filter((r) => filteredIds.includes(r.id));
                        setShownReviews(newBulkData);
                        setIsUpdatingReviews(false);
                        setIsLoadingReviews(false);
                    }
                });
            } else setShownReviews(bulkData);
        } else setShownReviews(bulkData);

        setBulkCounter(isNewBulk ? 1 : (bulkCounter+1));

        // return bulkData;
    }

    // Sort data via key
    const sortDataViaKeyHandler = (key, ascending=sortToggle) => {
        const sortedData = sortDataViaKey(key, ascending, filteredReviews);
        setFilteredReviews(sortedData);
        setSortBy({ key: key, ascend: ascending });
        setSortToggle(!ascending);
        // setShownReviews(sortedData);
        getBulkDataHandler(sortedData, true);
    }

    // Once edite clicked, fetch tags from server and open slide bar
    const tagQuotesHandler = (review) => {
        dispatch(getTags(review.id, 'reviews'));
        setSelectedReview(review);
        toggleSlideBar();
    }

    // On filters change handler, filter reviews, get bulked reviews and set shown reviews
    const onFiltersChangeHandler = (selectedFilters, searchVal = searchValue, reviewPool=allReviews) => {
        let filteredData = filterReviews(selectedFilters, searchVal, reviewPool);
        setFilteredReviews(filteredData);
        getBulkDataHandler(filteredData, true)
        // setShownReviews(bulk);
    }

    // Filter reviews based on selected reviews
    const filterReviews = (selectedFilters, searchVal, reviewPool=allReviews) => {
        if(!reviewPool.length) return {};

        let newlyFilteredReviews = reviewPool.filter((review) => {
            if (!Object.keys(selectedFilters.dropdown).length) {
                return true;
            } else {
                for (let filterName in selectedFilters.dropdown) {
                    switch (filterName) {
                        case 'company_name':
                            if (!review.company) {
                                return false;
                            }
                            break;
                        case 'timeframe':
                            let isIncluded = false;
                            selectedFilters.dropdown[filterName].map((timeFrame) => {
                                const selectedTimeFrame = timeFrame.replace(timeframeSuffix, "");
                                const formattedPastTime = formatDateForComparison(subtractDate(parseInt(selectedTimeFrame), 'months'));
                                if (isDateAfter(formatDateForComparison(review.published_date), formattedPastTime)) {
                                    isIncluded = true;
                                }
                            })
                            if (!isIncluded) {
                                return false;
                            }
                            break;
                        case 'strategic_themes':
                            if(!selectedFilters.dropdown[filterName].some( item => review[filterName].map(arr => arr[0]).includes(item))) {
                                return false;
                            }
                            break;
                        case 'sentiment':
                            if(!selectedFilters.dropdown[filterName].some((item) => Math.floor(parseFloat(review[filterName])) === parseFloat(item))) {
                                return false;
                            }
                            break;
                        case 'considered':
                            if (!selectedFilters.dropdown[filterName].some((item) => review[filterName].toString().includes(item))) {
                                return false;
                            }
                            break;
                        default:
                            if (!selectedFilters.dropdown[filterName].includes(review[filterName]?.toString())) {
                                return false;
                            }
                            break;
                    }
                }
                return true;
            }
        })

        if (searchVal && !searchViaElastic) {
            newlyFilteredReviews = newlyFilteredReviews.filter((review) => {
                let isIncluded = false;
                Object.keys(review.body).map((bodyHeader) => {
                    if (review.body[bodyHeader].toLowerCase().includes(searchVal.toLowerCase())) {
                        isIncluded = true;
                    }
                })
                return isIncluded;
            })
        }

        if(sortBy.key !== 'score') {
            // ie it's not the default
            newlyFilteredReviews = sortDataViaKey(sortBy.key, sortBy.ascend, newlyFilteredReviews);
        }

        populateFiltersMapping(newlyFilteredReviews, searchVal, selectedFilters);
        return newlyFilteredReviews;
    }

    // Once filter was selected update them
    const onFilterSelectHandler = (e, filterName, type, selectedOption=null) => {
        const selectedFiltersObject = onFilterSelect(e, filterName, type, selectedFilters, selectedOption);
        // const selectedFiltersObject = (newSelectedFilters === null) ? onFilterSelect(e, filterName, type, selectedFilters) :
        //     { dropdown: { ...selectedFilters.dropdown, [filterName]: newSelectedFilters }};
        // if(selectedFiltersObject.dropdown[filterName].length == 0) delete selectedFiltersObject.dropdown[filterName];
        setSelectedFilters(selectedFiltersObject);
        onFiltersChangeHandler(selectedFiltersObject);
    }

    // Handler for chips removal
    const onChipRemoveHandler = (filterKey, chip) => {
        const newSelectedFilters = onChipRemove(filterKey, chip, selectedFilters);
        onFiltersChangeHandler(newSelectedFilters);
    }

    // Slide bar toggle handler
    const toggleSlideBar = () => {
        setSlideBarToggle(!slideBarToggle);
    }

    // On delete click, set local state and dispatch to server delete request
    const onDeleteHandler = (quoteID) => {
        dispatch(deleteQuote(quoteID));
    }

    // Handler for search change
    const onSearchChange = (val) => {
        const selectedVal = val.length > 2 ? val : '';
        onFiltersChangeHandler(selectedFilters, selectedVal)
        setSearchValue(selectedVal);
    }

    // Handler for search change
    const toggleElasticSearch = async(val) => {
        if(val?.length > 2) {
            setSearchViaElastic(true);
            setSearchIcon('spinner fa-spin');
            await fetch(`/products/${props.product_id}/product_review_search.json?search=${val}`, {
                method: 'GET',
                headers: JSON_HEADERS
            }).then(function(response) {
                return response.json();
            }).then(function(jsonResponse) {
                setSearchValue(val);
                setSearchIcon('magnifying-glass');
                let filteredData = allReviews.filter((r) => jsonResponse.includes(r.id));
                setFilteredReviews(filteredData);
                populateFiltersMapping(filteredData, val, selectedFilters);
                getBulkDataHandler(filteredData, true);
            });
        }
    }

    const onReadReviewHandler = (review) => {
        // restricted review access
        setReviewAccessCount((c) => c + 1);
        setSelectedReview(review);
        setShowReviewModal(true);
    }

    const onNextReviewToggle = () => {
        if(selectedReview) {
            let orderedReviewIds = filteredReviews.map(r => r.id);
            let nextIndex = orderedReviewIds.indexOf(selectedReview.id) + 1;
            if(nextIndex < orderedReviewIds.length) {
                let reviewId = orderedReviewIds[nextIndex];
                if(reviewId) {
                    // restricted review access
                    setReviewAccessCount((c) => c + 1);
                    removeShareStatus();
                    // see if we have data for this review
                    let review = props.reviews.find((r) => r.id === reviewId) || allReviews.find((r) => r.id === reviewId);
                    setHasMoreReviews(nextIndex != (orderedReviewIds.length - 1));
                    if(review?.actions) {
                        setSelectedReviewHistory([...selectedReviewHistory, selectedReview]);
                        review && captureImpression(review.actions.url);
                        setSelectedReview(review);
                    } else {
                        fetchSingleReview(reviewId);
                    }
                    replaceReviewURL(reviewId);
                    return;
                }
            }
        }
        onCloseReviewModal()
    }

    const onPrevReviewToggle = () => {
        let readReviews = selectedReviewHistory.length;
        if(readReviews) {
            const prevReview = selectedReviewHistory[readReviews - 1];
            setSelectedReviewHistory(selectedReviewHistory.slice(0, -1));
            setSelectedReview(prevReview);
            setHasMoreReviews(true);
            replaceReviewURL(prevReview.id);
        } else onCloseReviewModal();
    }

    const onCloseReviewModal = (replaceURL=true) => {
        removeShareStatus();
        if(replaceURL) {
            history.back(); // reset history state since the browser's back button was not clicked
        }
        setSelectedReview(null);
        setSelectedReviewHistory([]);
        setShowReviewModal(false);
        setIsForwardClick(false);
        setShowRegistrationModal(false);
    }

    const replaceReviewURL = (reviewId=null) => {
        const queryParams = new URLSearchParams(location.search);
        if(reviewId) {
            queryParams.set('review_id', reviewId);
        } else {
            queryParams.delete('review_id');
            queryParams.delete('tid');
        }
        let newUrl = new URL(location);
        newUrl.search = queryParams;
        history.replaceState(null, '', newUrl.href);
    }

    const removeShareStatus = () => {
        if(isShareLink) {
            setIsShareLink(false);
        }
    }

    const updateReviewComments = (reviews, reviewId, comments, commentCount) => {
        if(!reviews.length) return []

        return reviews.map(review => {
            if (review.id === reviewId) {
                return { ...review, comments: comments, comment_count: commentCount };
            }
            return review;
        })
    };

    const onCommentsChangeHandler = (reviewId, comments, commentCount) => {
        setAllReviews(updateReviewComments(allReviews, reviewId, comments, commentCount));
        setFilteredReviews(updateReviewComments(filteredReviews, reviewId, comments, commentCount));
        setShownReviews(updateReviewComments(shownReviews, reviewId, comments, commentCount));
        if(selectedReview) setSelectedReview({ ...selectedReview, comments: comments, comment_count: commentCount });
    }

    const renderSearchResultsHdr = () => {
        if(searchValue) {
            return (
                <Row className={'mb-2'}>
                    <Col>
                        <span className={'font-16 fw-bolder text-secondary'}>Your search for <i>{searchValue}</i> returned {filteredReviews.length} results:</span>
                        <a className={'text-secondary ms-1'} onClick={() => onResetFilters()}>Reset search and filters</a>
                    </Col>
                </Row>
            )
        }
    }


    const renderReviews = () => {
        // console.log(shownReviews);
        return (
            shownReviews && shownReviews.map((review, index) => {
                return (
                    <div key={index}>
                        {props.report && index === 2 && <ReportBanner report={props.report} userCount={props.user_count} />}
                        <ReviewCard
                            review={review}
                            sections={props.review_sections}
                            showProduct={false}
                            showLikes={true}
                            showBody={true}
                            showProsCons={true}
                            // showComments={true}
                            // showNewCommentForm={true}
                            // onCommentsChangeHandler={onCommentsChangeHandler}
                            showProductVersion={true}
                            titleFont={22}
                            currentUser={currentUser}
                            token={props.token}
                            onReadReview={onReadReviewHandler}
                            showProductUserVerification={props.product.name}
                            showSentimentScore={false}
                            toggleRegistration={onShowRegistrationModal}
                        />
                    </div>
                )
            })
        )
    }

    const renderTooltip = () => {
        const link = sortingBase.find((sortingBaseItem) => sortingBaseItem.key === sortBy.key);

        if(link.tooltipText) {
            return (
              <PopoverTrigger content={link.tooltipText} placement='bottom' triggerIcon={'fa-question-circle'} triggerIconStyleClasses={'fal fs-5 ms-2'} />
            )
        }
    }

    const renderSortBy = () => {
        if(showFilterForm && filteredReviews.length) {
            return (
                <Row>
                    <Col xs={12} lg={{ span: 5, offset: 7 }} className={'d-flex align-items-center mb-3'}>
                        <div className={'font-16 fw-bolder gray-900 pe-2'}>Sort by:</div>
                        {allReviews.length ?
                          <>
                            <SelectFilter id={'sorting'} links={sortingBase} onFilterSelect={sortDataViaKeyHandler}
                                          extraClasses={'flex-fill'} reset={resetSort}/>
                              {renderTooltip()}
                          </>
                            :
                            <Placeholder animation={'glow'} className={'flex-fill'}>
                                <Placeholder as={Form.Control} aria-label="Loading..." disabled />
                            </Placeholder>
                        }
                    </Col>
                </Row>
            )
        }
    }

    const renderLoadingModal = () => {
        if(isUpdatingReviews) {
            return (
                <LoadingModal showModal={isUpdatingReviews} modalTxt={'Updating reviews...'}  />
            )
        }
    }

    const renderFilterTags = (classes) => {
        if(Object.keys(selectedFilters.dropdown).length) {
            return (
                <FiltersTags
                    selectedFilters={selectedFilters}
                    onChipRemove={onChipRemoveHandler}
                    classes={classes}
                />
            )
        }
    }

    const renderLoadMoreReviews = () => {
        if(shownReviews && filteredReviews.length > shownReviews.length) {
            return (
                <Row>
                    <Col xs={12} md={{ span: 4, offset: 4 }} lg={{ span: 6, offset: 3 }} xl={{ span: 4, offset: 4 }}>
                        <Button variant="secondary" onClick={() => getBulkDataHandler(filteredReviews)} className={'btn-block'}>
                            Load more reviews {isLoadingReviews && <i className={'fas fa-spinner fa-spin font-16 ms-1'}></i>}
                        </Button>
                    </Col>
                </Row>
            )
        }
    }

    const onLeadCreation = (action='', actionData, responseData={}) => {
        if(!currentUser && responseData.user) {
            setCurrentUser(responseData.user);
        }
        switch(action) {
            case 'AUDIO_HIGHLIGHT':
                // set up LeadGen with actionData.selected if we need phone verification, etc...
                setSelectedAudio(actionData.selected); // corresponds to the selected audio { url, type, reviewId }
                setShowAudioModal(true);
                return;
            case 'DOWNLOAD_REPORT':
            case 'INQUIRY_CALL':
            case 'PEERANALYST_USERS':
                return;
        }
    }

    const renderReviewModal = () => {
        if(showReviewModal) {
            return (
                <ReviewModal
                    review={selectedReview}
                    product={props.product}
                    relatedProducts={props.related_products}
                    reviewSections={props.review_sections}
                    show={showReviewModal}
                    report={props.report}
                    token={props.token}
                    reviewCount={props.product.review_count}
                    onCancel={onCloseReviewModal}
                    onNext={onNextReviewToggle}
                    onPrev={onPrevReviewToggle}
                    hasMoreReviews={hasMoreReviews}
                    onCommentsChangeHandler={onCommentsChangeHandler}
                    isShareLink={isShareLink}
                    onReplaceReviewURL={replaceReviewURL}
                    forwardClick={isForwardClick}
                    forwardModalToggle={handleReviewModalToggleOnForward}
                    restrictedAccess={showRegistrationModal}
                />
            )
        }
    }

    // audio
    const onCloseAudio = () => {
        setSelectedAudio({});
        setShowAudioModal(false);
    }

    const onToggleAudio = (url, type, reviewId = null) => {
        if (reviewId) {
            createLead({ leadType: 'ProductReviewLead', leadTypeData: reviewId, leadCode: "AUDIO_HIGHLIGHT", token: props.token });
        }
        setSelectedAudio({ url: url, type: type});
        setShowAudioModal(true);
    }

    const renderAudioModal = () => {
        if(showAudioModal) {
            return (
                <AudioModal
                    show={showAudioModal}
                    audioFile={selectedAudio.url}
                    mimeType={selectedAudio.type}
                    onHide={onCloseAudio}
                    text={'Listen to the interview'}
                />
            )
        }
    }
    
    // testing restricted review access

    const onHideRegistrationModal = (_) => {
        if(reviewAccessCount >= 2 && !currentUser) {
            onCloseReviewModal();
        }
        setShowRegistrationModal(false);
        if(currentUser) {
            setRegistrationRequired(false);
            setShowRegistrationConfirmation(true);
        }
    }

    const onShowRegistrationModal = () => {
        setShowRegistrationModal(true);
    }

    const renderRegistrationModal = () => {
        if(registrationRequired) {
            let subHeader = props.product.review_count < 20 ?
                `Get access to the full review and to ${props.category.review_count} more reviews for ${props.category.name}.` :
                `Get access to the full review and to ${props.product.review_count - 1} more ${props.product.name} reviews`;
            return (
                <RegistrationRoot
                    show={showRegistrationModal}
                    token={props.token}
                    gdpr={props.registration.gdpr}
                    modalHeader={'Sign up to continue reading'}
                    modalSubheader={subHeader}
                    allowSkip={reviewAccessCount < 2}
                    cta={`Join ${COMPANY_NAME}`}
                    onClose={onHideRegistrationModal}
                    onRegister={setCurrentUser}
                    reCaptchaKey={props.reCaptchaKey}
                    passwordRule={props.registration.password_rule}
                 />
            )
        }
    }

    const renderRegistrationConfirmation = () => {
        if(showRegistrationConfirmation) {
            return (
                <ResponseModal
                    bsPrefix={'modal-overlay'}
                    bgColor={'soft-success'}
                    textColor={'success'}
                    showIcon={false}
                    message={'Thank you for registering PeerSpot, you can now access all reviews across the site'}
                    show={showRegistrationConfirmation}
                    centered={false}
                    textAlignment={'start'}
                    btnClose={false}
                    btnColor={'success'}
                    borderColor={'success'}
                    dialogClasses={'modal-bottom-notice'}
                    showBackdrop={false}
                    autoClose={true}
                    handleShow={setShowRegistrationConfirmation} />
            )
        }
    }

    const onToggleLeadGen = (leadData={}) => {
        setLeadDetails({ ...leadData });
        setShowLeadGen(true);
        setTimeout(() => { setShowLeadGen(false) }, LEADGEN_TOGGLE_RESET_TIME);
    }

    const renderLeadGenModal = () => {
        // For external toggle lead gen only
        // This components gets passed in custom lead details, including:
        // Generic fields: hiddenTags, leadCode, cta, actionParams,  gtmEventRegion, gtmEventElement, modalOverlay, withConfirmationScreen
        // PeerTalk fields: actionParams, confirmationParams, modalHeader, modalSubheader, includePeerTalk
        // Report download fields: directReportUrl, directReportDownload, reportTopic, sendReport
        // Audio fields including: actionParams for the selected audio
        if(Object.keys(leadDetails).length) {
            return (
                <LeadGen
                    {...leadAttributes}
                    key={leadDetails?.leadCode}
                    token={props.token}
                    gdpr={props.registration.gdpr}
                    updateRoot={true}
                    currentUser={currentUser}
                    updateCurrentUser={setCurrentUser}
                    emailOnly={true}
                    externalToggle={true}
                    toggleLeadGen={showLeadGen}
                    modalHeader={'Get full access to PeerSpot'}
                    modalSubheader={'Register to unlock all PeerSpot features'}
                    afterLeadCreation={onLeadCreation}
                    redirect={false}
                    {...leadDetails}
                />
            )
        }
    }

    const onFilterToggleHandler = (isFilterOpen, filterName, toggleSource) => {
        if (!['select', 'rootClose'].includes(toggleSource)) {
            if (isFilterOpen) {
                setOpenFilter(filterName);
                setOpenFilterReviews(filteredReviews)
            } else {
                setOpenFilter(null);
                setOpenFilterReviews(null);
            }
        }
    }

    if (props.render_as === 'vendor_dashboard') {
        // Main component renderer
        return (
            <div id={"reviews"} className={"row"}>
                <SlideBar
                    isEnabled={slideBarToggle}
                    toggleSlideBar={toggleSlideBar}
                    direction={"left"}
                    size={"xl"}
                >
                    <ReviewTagging
                        review={selectedReview}
                        quotesLink={props.quotes_link}
                        hasQuotes={props.has_quotes}
                        onDeleteHandler={onDeleteHandler}
                        taggingEnabled={slideBarToggle}
                        cacheReviewID={cacheReviewID}
                        setCacheReviewID={setCacheReviewID}
                    />
                </SlideBar>

                <div className={"col-md-12 col-xl-2"}>
                    <Filters
                        data={filteredReviews}
                        selectedFilters={selectedFilters}
                        filtersMapping={filtersMapping}
                        onFilterSelect={onFilterSelectHandler}
                        onFilterToggleHandler={onFilterToggleHandler}
                        onSearchChange={onSearchChange}
                        filterHeader='Filter Reviews'
                        showSearchField={true}
                        showColumnHdr={true}
                        asCheckboxes={true}
                    />
                </div>

                <div className={"col-md-12 col-xl-10"}>
                    <div className={"card-box"}>
                        <Row className={'align-items-center'}>
                            <div className={'col-10'}>
                                <h4 className={'header-title pr-3'}>Search {filteredReviews.length} reviews to find and
                                    tag quotes</h4>
                            </div>
                            <div className={'text-end'}>
                                <Button variant="secondary" href={props.csv_link}>
                                    <i className={"fas fa-download me-1"}/>
                                    Download Reviews
                                </Button>
                            </div>
                        </Row>


                        <div className={'d-flex flex-row'}>
                            {renderFilterTags('mb-2 mt-3')}
                        </div>

                        <ReviewTableList
                            reviews={shownReviews}
                            sortDataViaKeyHandler={sortDataViaKeyHandler}
                            searchValue={searchValue}
                            selectedFilters={selectedFilters}
                            tagQuotesHandler={tagQuotesHandler}
                        />

                        <Row>
                            <Col sm={12} md={4} className={'pt-3'}>
                                {loadMoreHandler(shownReviews, filteredReviews, getBulkDataHandler)}
                            </Col>
                        </Row>

                    </div>
                </div>
            </div>
        )
    } else if(props.render_as === 'research') {
        return (
            <>
                {props.includeFilters && <Row className={'mb-3'} style={ isDesktop ? { marginRight: '-24px', marginLeft: '-24px' } : {}}>
                    <Filters
                        data={filteredReviews}
                        selectedFilters={selectedFilters}
                        filtersMapping={filtersMapping}
                        onFilterSelect={onFilterSelectHandler}
                        onSearchChange={onSearchChange}
                        filterHeader='Filter Reviews'
                        showSearchField={false}
                        showColumnHdr={false}
                        showPlaceholder={false}
                        asCheckboxes={false}
                        gtmEventRegion={props.gtmEventRegion}
                    />
                </Row>}
                <ReviewResearchTable
                    reviews={shownReviews}
                    product={props.product}
                    sortDataViaKeyHandler={sortDataViaKeyHandler}
                    searchValue={searchValue}
                    researchType={props.researchType}
                />
                <Row>
                    <Col sm={12} md={4} className={'pt-3 px-md-0'}>
                        {loadMoreHandler(shownReviews, filteredReviews, getBulkDataHandler, props.researchType, props.gtmEventRegion)}
                    </Col>
                </Row>
            </>
        )
    }
    else {
        const contextValue = {
            leadAttributes,
            selectedAudio,
            currentUser,
            setCurrentUser,
            tooltips,
            onLeadCreation,
            onToggleAudio,
            onToggleLeadGen }
        return (
            <ReviewsContext.Provider value={ contextValue }>
                <Row ref={reviewsContainerRef}>
                    <Col xs={10} lg={12}>
                        <h2 className={'text-secondary fs-md-4 fw-bolder font-family-secondary pb-3 mt-0'} style={{fontSize: '18px'}}>{props.reviews_hdr}</h2>
                    </Col>
                    <Col xs={2} lg={false} className={'d-lg-none text-end'}>
                        <Button type="button" variant="white" size="sm" className={'btn-block'} onClick={() => setShowFilterForm(!showFilterForm)}>
                            <i className={'fal fa-bars-filter'}></i>
                        </Button>
                    </Col>
                </Row>
                {/*{renderLoadingReviews()}*/}
                <Row id={'reviews'}>
                    {/*after enable mobile filters on modal, add classes 'sticky-top sticky-topbar pt-3 bg-light position-lg-relative' to the row above*/}
                    <Col xs={12} lg={4} xl={3}>
                        <Collapse in={showFilterForm} className={'sticky-md-top sticky-topbartabs'}>
                            <div id={'mobileToggleFilter'}>
                                {filteredReviews.length && <div className={'mb-3'}>
                                    <SearchBar
                                        placeholder={`Search...`}
                                        onSearchSubmit={toggleElasticSearch}
                                        extraClasses={''}
                                        inputValue={searchValue}
                                        searchIcon={searchIcon}
                                        showPlaceholder={!allReviews.length}
                                    />
                                </div>}
                                <Filters
                                    data={filteredReviews}
                                    selectedFilters={selectedFilters}
                                    filtersMapping={filtersMapping}
                                    onFilterSelect={onFilterSelectHandler}
                                    onFilterToggleHandler={onFilterToggleHandler}
                                    onSearchChange={onSearchChange}
                                    filterHeader='Filter Reviews'
                                    showSearchField={false}
                                    showColumnHdr={false}
                                    showPlaceholder={true}
                                    asCheckboxes={true}
                                />
                            </div>
                        </Collapse>
                    </Col>

                    <Col xs={12} lg={8} xl={9}>
                        {renderSortBy()}
                        {renderFilterTags('mb-3')}
                        {renderSearchResultsHdr()}
                        {renderReviews()}
                        {renderLoadMoreReviews()}
                    </Col>
                </Row>
                {renderLoadingModal()}
                {renderReviewModal()}
                {renderRegistrationModal()}
                {renderRegistrationConfirmation()}
                {renderAudioModal()}
                {renderLeadGenModal()}
            </ReviewsContext.Provider>
        )
    }
}
Reviews.displayName = 'Reviews';
export default Reviews;
